import React, { useState } from 'react';

import { Button, Flex } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { ChipFilters, Filter } from 'components/ChipFilters';
import { DashboardPageWrapper } from 'components/DashboardPageWrapper';
import { ErrorAlert } from 'components/ErrorAlert';
import { SmartAssistantLoadingSkeleton } from 'components/LoadingSkeletons/SmartAssistantLoadingSkeleton';
import { NoDataFound } from 'components/NoDataFound';
import { SearchBar } from 'components/SearchBar';
import { SmartAssistantCard } from 'components/SmartAssistantCard';
import { TeamSelectFilter } from 'components/TeamSelectFilter';
import { VirtualGridScrollArea } from 'components/VirtualGridScrollArea';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { AgentIcon } from 'icons/AgentIcon';
import { CreateSmartAssistantDrawer } from 'pages/CreateSmartAssistant/CreateSmartAssistantDrawer';
import { useTranslation } from 'react-i18next';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { TeamService } from 'services/team-service';
import { UserService } from 'services/user-service';
import { HIDE_ADD_AGENT_BUTTON_FOR_PRODUCT_NAMES } from 'shared/constants/organisation';
import { SMART_ASSISTANTS_QUERY_KEY } from 'shared/constants/query-keys';
import { MY_SMART_ASSISTANTS_FILTER } from 'shared/constants/smart-assistant';
import { SmartAssistant, SmartAssistantType } from 'shared/interfaces/SmartAssistant';

const LOCAL_STORAGE_FILTER_KEY = 'smart-assistant-team-filter';

export function SmartAssistantsPage() {
  const { t } = useTranslation();

  const [tagFilters, setTagFilters] = useState(new Set<string>([]));
  const [selectedTagFilters, setSelectedTagFilters] = useState(new Set<string>([]));
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<Filter<SmartAssistantType>[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<Filter<SmartAssistantType>>();
  const [subscription] = useSubscriptionContext();
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>(
    localStorage.getItem(LOCAL_STORAGE_FILTER_KEY) ?? MY_SMART_ASSISTANTS_FILTER
  );
  const [createSmartAssistantDrawerOpened, setCreateSmartAssistantDrawerOpened] = useState(false);
  const openCreateSmartAssistantDrawer = () => setCreateSmartAssistantDrawerOpened(true);
  const closeCreateSmartAssistantDrawer = () => setCreateSmartAssistantDrawerOpened(false);

  const filteredSmartAssistantsQuery = useQuery({
    queryKey: [SMART_ASSISTANTS_QUERY_KEY, selectedTeamFilter],
    queryFn: async () => {
      if (selectedTeamFilter === MY_SMART_ASSISTANTS_FILTER) {
        return SmartAssistantService.getAllSmartAssistantsCreatedByMe();
      } else if (UserService.isCustomOrganisationMember()) {
        const teams = await TeamService.getMemberTeams();
        const team = teams.find((team) => team.name === selectedTeamFilter);
        if (team) {
          return SmartAssistantService.getAllSmartAssistantsByTeam(team._id);
        } else {
          setSelectedTeamFilterAndUpdateLocalStorage(MY_SMART_ASSISTANTS_FILTER);
        }
      }

      return [];
    },
    onSuccess: (smartAssistants: SmartAssistant[]) => {
      const tags = new Set<string>();
      const types = new Set<SmartAssistantType>();
      for (const smartAssistant of smartAssistants) {
        smartAssistant.tags.forEach((tag) => tags.add(tag));
        types.add(smartAssistant.type);
      }
      setTagFilters(tags);
      setFilters(
        Array.from(types)
          .sort((a, b) => a.localeCompare(b))
          .map((type) => ({ label: t(`SMART_ASSISTANT.TYPE.${type.toUpperCase()}.TITLE`), value: type }))
      );
    }
  });

  const setSelectedTeamFilterAndUpdateLocalStorage = (teamFilter: string) => {
    if (teamFilter !== MY_SMART_ASSISTANTS_FILTER) {
      localStorage.setItem(LOCAL_STORAGE_FILTER_KEY, teamFilter);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_FILTER_KEY);
    }
    setSelectedTeamFilter(teamFilter);
  };

  const hideCreateButton = HIDE_ADD_AGENT_BUTTON_FOR_PRODUCT_NAMES.includes(subscription.product.name);
  const CreateButton = () => {
    if (hideCreateButton) return null;

    return (
      <Button leftIcon={<IconPlus />} onClick={openCreateSmartAssistantDrawer}>
        {t('APP.ACTIONS.ADD')}
      </Button>
    );
  };

  if (filteredSmartAssistantsQuery.isError) return <ErrorAlert />;

  const smartAssistantsLoading = filteredSmartAssistantsQuery.isLoading;
  const noSmartAssistants = !smartAssistantsLoading && filteredSmartAssistantsQuery.data.length === 0;

  const filteredSmartAssistants = filteredSmartAssistantsQuery.data
    ? filteredSmartAssistantsQuery.data
        .filter((smartAssistant) =>
          selectedTagFilters.size > 0 ? smartAssistant.tags.some((tag) => selectedTagFilters.has(tag)) : true
        )
        .filter((smartAssistant) =>
          searchValue ? smartAssistant.name.toLowerCase().includes(searchValue.toLowerCase()) : true
        )
        .filter((smartAssistant) => (selectedFilter?.value ? smartAssistant.type === selectedFilter.value : true))
    : [];

  return (
    <DashboardPageWrapper
      icon={<AgentIcon w={25} h={25} />}
      title={t('APP.DASHBOARD_SECTIONS.SMART_ASSISTANTS')}
      rightComponent={
        <Flex direction="row" align="center" gap="md">
          {UserService.isCustomOrganisationMember() && (
            <TeamSelectFilter
              teamFilter={selectedTeamFilter}
              setTeamFilter={setSelectedTeamFilterAndUpdateLocalStorage}
              myFilter={MY_SMART_ASSISTANTS_FILTER}
            />
          )}
          {!noSmartAssistants && <CreateButton />}
        </Flex>
      }>
      <Flex h="100%" direction="column">
        {(smartAssistantsLoading || filteredSmartAssistantsQuery.data.length > 0) && (
          <>
            <ChipFilters
              py={10}
              filters={filters}
              selectedFilter={selectedFilter}
              setSelectedFilter={(filter) => setSelectedFilter(filter)}
            />

            <SearchBar
              mb={15}
              filters={tagFilters}
              selectedFilters={selectedTagFilters}
              setSelectedFilters={setSelectedTagFilters}
              onFilterCreate={() => filteredSmartAssistantsQuery.refetch()}
              onSearchValueChange={setSearchValue}
              placeholder={t('SEARCH_BAR.SMART_ASSISTANT_PLACEHOLDER')}
              smartAssistants={filteredSmartAssistantsQuery.data}
            />
          </>
        )}
        {smartAssistantsLoading ? (
          <SmartAssistantLoadingSkeleton />
        ) : (
          <VirtualGridScrollArea
            h={noSmartAssistants || filteredSmartAssistants.length == 0 ? 0 : '100%'}
            items={filteredSmartAssistants.map((smartAssistant: SmartAssistant) => {
              return (
                <SmartAssistantCard
                  key={smartAssistant._id}
                  mb={15}
                  mr={15}
                  smartAssistant={smartAssistant}
                  selectedTeamFilter={selectedTeamFilter}
                  allTags={Array.from(tagFilters)}
                />
              );
            })}
          />
        )}

        {noSmartAssistants && (
          <NoDataFound
            h="100%"
            title={t('NO_DATA_FOUND.SMART_ASSISTANTS.TITLE')}
            subTitle={t('NO_DATA_FOUND.SMART_ASSISTANTS.SUB_TITLE')}
            icon={<AgentIcon w={20} h={20} />}
            button={<CreateButton />}
          />
        )}

        {!noSmartAssistants && filteredSmartAssistants.length == 0 && (selectedTagFilters.size > 0 || searchValue) && (
          <NoDataFound h="100%" />
        )}
      </Flex>

      <CreateSmartAssistantDrawer opened={createSmartAssistantDrawerOpened} onClose={closeCreateSmartAssistantDrawer} />
    </DashboardPageWrapper>
  );
}
